import { MdShowChart } from 'react-icons/md';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AgentApiAnalysisSalesSeries } from '@/types/serializers';
import InsightEmptyState from '../InsightEmptyState';
import SalesRegisterButton from './SalesRegisterButton';
import {
  AXIS_LABEL_COLOR,
  AXIS_TICK_COLOR,
  CHART_FOCUS_RESET,
  GRID_COLOR,
  formatPeriodLabel,
  formatYen,
  trendColorForIndex,
} from './salesChartUtils';

interface Props {
  title: string;
  series: AgentApiAnalysisSalesSeries;
}

type TooltipPayloadItem = { dataKey?: string | number; value?: number | string; color?: string };
type TooltipProps = { active?: boolean; payload?: TooltipPayloadItem[]; label?: string };

function LineTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="flex flex-col gap-xxs rounded-sm bg-white p-xs text-body-xs text-main shadow-layer2">
      {label && <span className="font-bold">{formatPeriodLabel(label)}</span>}
      {payload.map((p) => (
        <div key={String(p.dataKey)} className="flex items-center gap-xxs">
          <span
            className="inline-block size-3 shrink-0 rounded-xs"
            style={{ backgroundColor: p.color }}
          />
          <span>{String(p.dataKey)}</span>
          <span className="font-bold">{formatYen(Number(p.value) || 0)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * bucket × グループの折れ線グラフ（ポジション別売上金額トレンド）。
 */
export default function SalesSeriesLineChart({ title, series }: Props) {
  const hasData = series.series.length > 0;

  const data = series.points.map((p) =>
    series.series.reduce<Record<string, unknown>>(
      (acc, name) => {
        acc[name] = p.grouped_values[name] ?? 0;
        return acc;
      },
      { period: p.period }
    )
  );

  return (
    <div className={`w-full ${CHART_FOCUS_RESET}`}>
      <div className="flex items-start justify-between gap-md pb-sm">
        <h3 className="m-0 text-heading-xs text-main">{title}</h3>
        {!hasData && <SalesRegisterButton />}
      </div>

      {hasData ? (
        <>
          <div className="mb-sm flex flex-wrap items-center gap-x-md gap-y-xxs text-body-sm text-main">
            {series.series.map((name, idx) => (
              <div key={name} className="flex items-center gap-xs">
                <span
                  className="inline-block size-[18px] shrink-0 rounded-sm"
                  style={{ backgroundColor: trendColorForIndex(idx) }}
                />
                {name}
              </div>
            ))}
          </div>

          <ResponsiveContainer width="100%" height={340}>
            <LineChart data={data} margin={{ top: 16, right: 24, left: 8, bottom: 12 }}>
              <CartesianGrid stroke={GRID_COLOR} strokeDasharray="4 4" vertical={false} />
              <XAxis
                dataKey="period"
                tickFormatter={formatPeriodLabel}
                tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                width={72}
                tickFormatter={(v: number) => v.toLocaleString('ja-JP')}
                tick={{ fontSize: 12, fill: AXIS_LABEL_COLOR }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<LineTooltip />} />
              {series.series.map((name, idx) => (
                <Line
                  key={name}
                  type="monotone"
                  dataKey={name}
                  stroke={trendColorForIndex(idx)}
                  strokeWidth={2}
                  dot={{ r: 2, fill: trendColorForIndex(idx) }}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </>
      ) : (
        <InsightEmptyState
          icon={MdShowChart}
          description="データを登録すると、ここにグラフが表示されます"
        />
      )}
    </div>
  );
}
